import { useState, useMemo, useCallback } from "react";
import { useDebounce } from "../../../../../hooks/useDebounce";
import LimitSelect from "../../../../../components/elements/LimitSelect";
import Search from "../../../../../components/form/Search";
import Table from "../../../../../components/elements/table/Table";
import dayjs from "dayjs";
import { handleFilterChange } from "../../../../../helpers";
import { tableFilter } from "../../../../../helpers/tableFilterOptions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ManageOrderDrawer from "../../../../../components/sections/front/Order/Drawer/ManageOrderDrawer";
import { useOutletContext } from "react-router-dom";
import { useOrganization } from "../../../../../context/OrganizationContext";
import * as XLSX from "xlsx";
import { orderReportList } from "../../../../../services/eventReportService";
import ExportDropdown from "../ExportDropdown";
import OrderDrawer from "./OrderDrawer";
import Button from "../../../../form/Button";
import {
  reportCountList,
  reportExport,
} from "../../../../../services/reportService";
import {
  createCSVContent,
  createExcelFile,
  downloadFile,
} from "../CommonExport";
import Skeleton from "../../../../loader/Skeleton";
import { t } from "i18next";

// Constants moved outside component to prevent recreation
const PAYMENT_STATUS_LIST = [
  { name: "pending", value: "pending", type: "checkbox" },
  { name: "Success", value: "success", type: "checkbox" },
  { name: "failed", value: "failed", type: "checkbox" },
];

const STATUS_LIST = [
  { name: "processing", value: "processing", type: "checkbox" },
  { name: "canceled", value: "canceled", type: "checkbox" },
  { name: "confirmed", value: "confirmed", type: "checkbox" },
  { name: "cancel-request", value: "cancel-request", type: "checkbox" },
  { name: "partial-refund", value: "partial-refund", type: "checkbox" },
];

const STATUS_STYLES = {
  processing: {
    backgroundColor: "#FEF3C7",
    color: "#D97706",
    borderColor: "#F59E0B",
  },
  shipped: {
    backgroundColor: "#DBEAFE",
    color: "#1D4ED8",
    borderColor: "#3B82F6",
  },
  pickup: {
    backgroundColor: "#FCE7F3",
    color: "#BE185D",
    borderColor: "#EC4899",
  },
  delivered: {
    backgroundColor: "#D1FAE5",
    color: "#047857",
    borderColor: "#10B981",
  },
  confirmed: {
    backgroundColor: "#ECFDF5",
    color: "#065F46",
    borderColor: "#059669",
  },
  canceled: {
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
    borderColor: "#EF4444",
  },
  "return-request": {
    backgroundColor: "#FDF4FF",
    color: "#7C2D92",
    borderColor: "#A855F7",
  },
  rejected: {
    backgroundColor: "#F3F4F6",
    color: "#374151",
    borderColor: "#6B7280",
  },
};

const PAYMENT_STATUS_STYLES = {
  pending: {
    backgroundColor: "#FEF3C7",
    color: "#D97706",
    borderColor: "#F59E0B",
  },
  success: {
    backgroundColor: "#D1FAE5",
    color: "#047857",
    borderColor: "#10B981",
  },
  failed: {
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
    borderColor: "#EF4444",
  },
};

const DEFAULT_STYLE = {
  backgroundColor: "#F3F4F6",
  color: "#374151",
  borderColor: "#6B7280",
};

const getStatusStyle = (status) => {
  return STATUS_STYLES[status?.toLowerCase()] || DEFAULT_STYLE;
};

const getPaymentStatusStyle = (status) => {
  return PAYMENT_STATUS_STYLES[status?.toLowerCase()] || DEFAULT_STYLE;
};

const OrderList = () => {
  const { event } = useOutletContext();
  const { selectedOrganization } = useOrganization();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState({ isOpen: false, data: null });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [additionalFilters, setAdditionalFilters] = useState();
  const [eventCountFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const {
    data: eventCount,
    isLoading: eventCountLoading,
    refetch: eventRefetch,
  } = useQuery({
    queryKey: ["eventCountData", eventCountFilters, event?.data?._id],
    queryFn: async () => {
      const res = await reportCountList({
        ...eventCountFilters,
        ...additionalFilters,
        event: [event?.data?._id],
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
    enabled: !!event?.data?._id,
  });

  // Memoized query key
  const queryKey = useMemo(
    () => ["order", filters, search, selectedOrganization, event?.data?._id],
    [filters, search, selectedOrganization, event?.data?._id]
  );

  // Fetch orders data
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await orderReportList({
        ...filters,
        ...(search && { keyword: search }),
        ...additionalFilters,
        organization: selectedOrganization,
        event: event?.data?._id,
        viewAs: "seller",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!event?.data?._id,
  });

  // Send file to API
  const sendFileToAPI = useCallback(
    async ({ fileContent, localDownloadStatus, fileName, format, event }) => {
      try {
        const formData = new FormData();
        formData.append("event", event);
        formData.append("file", fileContent, fileName);
        formData.append("type", "orders");
        formData.append("name", `Orders Report (${format?.toUpperCase()})`);
        formData.append("organization", selectedOrganization);
        formData.append("status", localDownloadStatus ? "success" : "failed");

        const res = await reportExport(formData);
        return res?.status === 200 || res?.status === 201;
      } catch (error) {
        toast.warning(
          "File downloaded successfully, but failed to send to server"
        );
        return false;
      }
    },
    [selectedOrganization]
  );

  // Unified export handler
  const handleExport = useCallback(
    async (format) => {
      setIsExportOpen(false);
      toast.info("Preparing export data...");

      try {
        const exportResponse = await orderReportList({
          ...filters,
          ...(search && { keyword: search }),
          limit: data?.metadata?.totalDocs,
          page: 1,
          organization: selectedOrganization,
          event: event?.data?._id,
          orderStatus: ["confirmed"],
        });

        if (exportResponse?.docs?.status !== 200) {
          toast.error(exportResponse?.message || "Failed to fetch export data");
          return;
        }

        const orderData = exportResponse?.docs?.data || [];
        if (!orderData || orderData.length === 0) {
          toast.warning("No data available to export");
          return;
        }

        // Transform data
        const exportData = orderData.map((order) => ({
          "Order ID": order.orderId || "-",
          "Paid Amount": `${order.total.toFixed(2).toString() || 0}`,
          "Customer Name":
            `${order.userData?.firstName || ""} ${
              order.userData?.lastName || ""
            }`.trim() || "-",
          "Order Date": order.createdAt
            ? dayjs(order.createdAt).format("MM-DD-YYYY")
            : "-",
          "Payment Status": order.payment?.status || "pending",
          "Order Status": order.status?.info || "-",
        }));

        const filename = `orders_export_${dayjs().format("YYYY-MM-DD")}`;

        let blob, fullFilename;
        if (format === "csv") {
          const csvContent = createCSVContent(exportData);
          blob = new Blob([csvContent], { type: "text/csv" });
          fullFilename = `${filename}.csv`;
        } else {
          const workbook = createExcelFile(exportData);
          if (format === "xlsx") {
            const excelBuffer = XLSX.write(workbook, {
              bookType: "xlsx",
              type: "array",
            });
            blob = new Blob([excelBuffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            fullFilename = `${filename}.xlsx`;
          }
        }

        const downloadSuccess = await downloadFile(blob, fullFilename);

        if (downloadSuccess) {
          toast.success(
            `${format.toUpperCase()} file ${
              window.showSaveFilePicker ? "saved" : "downloaded"
            } successfully!`
          );

          try {
            await sendFileToAPI({
              fileName: fullFilename,
              fileContent: blob,
              localDownloadStatus: downloadSuccess,
              format: format,
              event: event?.data?._id,
            });
          } catch (apiError) {
            console.error("API upload failed:", apiError);
            if (window.showSaveFilePicker) {
              toast.warning("File saved locally but failed to sync to server");
            }
          }
        }
      } catch (error) {
        toast.error("Export failed. Please try again.");
      }
    },
    [
      filters,
      search,
      data?.metadata?.totalDocs,
      selectedOrganization,
      event?.data?._id,
      createCSVContent,
      createExcelFile,
      downloadFile,
      sendFileToAPI,
    ]
  );

  // Memoized table head data
  const tableHeadData = [
    {
      _id: "OrderId",
      name: t("Order Id"),
      align: "left",
    },
    {
      _id: 1,
      name: t("Paid Amount"),
      align: "left",
    },
    {
      _id: 3,
      name: t("Order by"),
      align: "left",
    },
    {
      _id: 4,
      name: t("Order date"),
      align: "left",
    },
    {
      _id: "paymentStatus",
      name: t("payment Status"),
      align: "left",
      ...tableFilter({
        filterKey: "paymentStatus",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [...PAYMENT_STATUS_LIST],
      }),
    },
    {
      _id: 5,
      name: t("Order Status"),
      align: "left",
      ...tableFilter({
        filterKey: "status",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [...STATUS_LIST],
      }),
    },
    {
      _id: 8,
      name: t("Action"),
      align: "left",
    },
  ];

  // Memoized table data with optimized rendering
  const tableData = useMemo(() => {
    return (
      data?.data?.map((item) => {
        const statusText =
          item?.status?.info?.charAt(0)?.toUpperCase() +
            item?.status?.info?.slice(1) || "-";
        const statusStyle = getStatusStyle(statusText);

        const paymentStatus = item?.payment?.status || "pending";
        const paymentStatusText =
          paymentStatus.charAt(0)?.toUpperCase() + paymentStatus.slice(1);
        const paymentStatusStyle = getPaymentStatusStyle(paymentStatus);

        return {
          _id: item?._id,
          rowData: [
            {
              _id: 1,
              type: "text",
              align: "left",
              cellClass: "!min-w-[100px] text-purple-600 font-medium",
              data: item?.orderId || "-",
            },
            {
              _id: 2,
              type: "text",
              align: "left",
              cellClass: "!min-w-[100px] font-medium",
              data: `${item?.eventData?.currency?.symbol || "$"} ${item?.total
                .toFixed(2)
                .toString()}`,
            },
            {
              _id: 4,
              type: "text",
              align: "left",
              cellClass: "!min-w-[100px] text-purple-600",
              data:
                item?.userData?.firstName || item?.userData?.lastName
                  ? `${item?.userData?.firstName ?? ""} ${
                      item?.userData?.lastName ?? ""
                    }`.trim()
                  : "-",
            },
            {
              _id: 5,
              type: "text",
              align: "left",
              cellClass: "!min-w-[100px] text-gray-600",
              data:
                dayjs(item?.createdAt).format("MMM DD, YYYY hh:mm A") || "-",
            },
            {
              _id: "paymentStatus",
              type: "text",
              align: "left",
              cellClass: "!min-w-[100px]",
              data: (
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
                  style={paymentStatusStyle}
                >
                  {paymentStatusText}
                </span>
              ),
            },
            {
              _id: 6,
              type: "text",
              align: "left",
              cellClass: "!min-w-[100px]",
              data: (
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
                  style={statusStyle}
                >
                  {statusText}
                </span>
              ),
            },
            {
              _id: "action",
              type: "action",
              align: "left",
              cellClass: "w-[5%]",
              actionData: [
                {
                  _id: "details",
                  name: t("Details"),
                  icon: "fa-regular fa-circle-info",
                  onClick: () => {
                    setModalOpen({ isOpen: true, data: item });
                  },
                  standout: false,
                },
              ],
            },
          ],
        };
      }) || []
    );
  }, [data?.data]);

  // Memoized callbacks
  const pageManageResponse = useCallback(
    (res) => {
      if (res?.status === 200 || res?.status === 201) {
        if (res?.status === 201) {
          if (filters?.page === 1) {
            queryClient.setQueryData(queryKey, (state) => {
              if (state) {
                const stateData = [...(state?.data || []), res?.data];
                return { ...state, data: stateData };
              }
            });
            if (data?.metadata?.totalDocs > filters?.limit) {
              queryClient.invalidateQueries(queryKey);
            }
          } else {
            setFilters((pre) => ({ ...pre, page: 1 }));
          }
        }
        if (res?.status === 200) {
          queryClient.setQueryData(queryKey, (state) => {
            if (state) {
              const stateData = state?.data?.map((item) =>
                item?._id === res?.data?._id ? { ...item, ...res?.data } : item
              );
              return { ...state, data: stateData };
            }
          });
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(queryKey);
          }
        }
        setModalOpen({ isOpen: false, data: null });
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    [
      filters?.page,
      filters?.limit,
      data?.metadata?.totalDocs,
      queryClient,
      queryKey,
    ]
  );

  const handleRunReport = useCallback(async () => {
    try {
      setDrawerOpen({ isOpen: false, data: null });
      await refetch();
      await eventRefetch();
    } catch (error) {
      toast.error("Failed to generate report. Please try again.");
    }
  }, [refetch]);

  const handleLimitChange = useCallback((val) => {
    handleFilterChange("limit", val, setFilters);
  }, []);

  const handleSearch = useCallback((val) => {
    setKeyword(val);
    handleFilterChange("page", 1, setFilters);
  }, []);

  const handlePageChange = useCallback((val) => {
    handleFilterChange("page", val, setFilters);
    setSelectedItems([]);
  }, []);

  const toggleExportDropdown = useCallback(() => {
    setIsExportOpen(!isExportOpen);
  }, [isExportOpen]);

  const openFilterDrawer = useCallback(() => {
    setDrawerOpen((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen({ isOpen: false, data: null });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen({ isOpen: false, data: null });
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Button
            buttonLabel={t("Filters")}
            buttonIcon={"fas fa-filter text-sm"}
            buttonFunction={openFilterDrawer}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-gray-600 text-sm font-medium mb-2">
            {t("Net Sales")}
          </h3>
          <div className="text-2xl md:text-3xl font-bold text-gray-900">
            {eventCountLoading ? (
              <Skeleton width={120} height={36} />
            ) : eventCount?.netSales ? (
              `${
                event?.data?.currency?.symbol || "$"
              } ${eventCount?.netSales?.toFixed(2)}`
            ) : (
              0
            )}
          </div>
        </div>

        {/* Tickets + Add-Ons Sold */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-gray-600 text-sm font-medium mb-2">
            {t("Tickets + Add-Ons Sold")}
          </h3>
          <div className="text-2xl md:text-3xl font-bold text-gray-900">
            {eventCountLoading ? (
              <Skeleton width={60} height={36} />
            ) : eventCount ? (
              eventCount?.ticketsSold + eventCount?.addonsSold
            ) : (
              0
            )}
          </div>
        </div>

        {/* Orders */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-gray-600 text-sm font-medium mb-2">
            {t("Orders")}
          </h3>
          <div className="text-2xl md:text-3xl font-bold text-gray-900">
            {eventCountLoading ? (
              <Skeleton width={30} height={36} />
            ) : (
              eventCount?.totalOrder
            )}
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="flex flex-wrap gap-3 md:gap-auto items-center justify-between">
        <div className="flex flex-wrap items-center gap-1 md:gap-3">
          <div className="w-full md:w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={handleLimitChange}
            />
          </div>
          <div className="w-full md:w-72 mt-3">
            <Search placeholder={t("Search here")} search={handleSearch} />
          </div>
        </div>
        <div className="flex w-full md:w-auto gap-4 items-center">
          <ExportDropdown
            onExport={handleExport}
            isOpen={isExportOpen}
            onToggle={toggleExportDropdown}
          />
        </div>
      </div>

      {/* Orders Table */}
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={handlePageChange}
        originalData={[...(data?.data || [])]}
        setOriginalData={(val) => {
          queryClient.setQueryData(queryKey, (state) => {
            if (state) {
              return { ...state, data: val };
            }
          });
        }}
        isFetching={isFetching}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
      />

      {/* Modals */}
      <ManageOrderDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={closeModal}
        getResponse={pageManageResponse}
      />
      <OrderDrawer
        open={drawerOpen.isOpen}
        close={closeDrawer}
        filters={additionalFilters}
        setFilters={setAdditionalFilters}
        onRunReport={handleRunReport}
      />
    </div>
  );
};

export default OrderList;
